import { InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashboardApi } from "../../../modules/dashboard/api";
import {
  CreateUpdateDashboardContact,
  CreateUpdateDashboardNotice,
  DashboardContactSearchRequest,
  DashboardMinMaxDatesRequest,
  DashboardNoticeFilterPageRequest,
  DashboardNoticeFilterPageResult
} from "../../../modules/dashboard/types";
import { PageSizes } from "../../constants";
import { EntityObject, TwoLevelEntityIdObject } from "../../types";
import messageUtils from "../../utils/messageUtils";
import { openBlobFile } from "../../utils/utils";

const ONE_HOUR = 60 * 60 * 1000;

export const DASHBOARD_QUERY_KEYS = {
  getDashboardStatistics: () => ["getDashboardStatistics"],
  getDashboardPersonalDates: (payload?: DashboardMinMaxDatesRequest) =>
    payload ? ["getDashboardPersonalDates", payload] : ["getDashboardPersonalDates"],
  getDashboardAgentCompetences: () => ["getDashboardAgentCompetences"],
  getDashboardContractStatuses: () => ["getDashboardContractStatuses"],
  getDashboardContractStatistics: (payload?: DashboardMinMaxDatesRequest) =>
    payload ? ["getDashboardContractStatistics", payload] : ["getDashboardContractStatistics"],
  filterDashboardNotices: (filter?: DashboardNoticeFilterPageRequest) =>
    filter ? ["filterDashboardNotices", filter] : ["filterDashboardNotices"],
  createDashboardNotice: () => ["createDashboardNotice"],
  updateDashboardNotice: () => ["updateDashboardNotice"],
  deleteDashboardNotice: () => ["deleteDashboardNotice"],
  addDashboardNoticeAttachments: () => ["addDashboardNoticeAttachments"],
  downloadDashboardNoticeAttachment: () => ["downloadDashboardNoticeAttachment"],
  deleteDashboardNoticeAttachment: () => ["deleteDashboardNoticeAttachment"],
  searchDashboardContacts: (filter?: DashboardContactSearchRequest) =>
    filter ? ["searchDashboardContacts", filter] : ["searchDashboardContacts"],
  createDashboardContact: () => ["createDashboardContact"],
  updateDashboardContact: () => ["updateDashboardContact"],
  deleteDashboardContact: () => ["deleteDashboardContact"]
};

export const useGetDashboardStatistics = () => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardStatistics(),
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardStatistics();

      return data;
    },
    staleTime: ONE_HOUR
  });
};

export const useGetDashboardPersonalDates = (payload: DashboardMinMaxDatesRequest) => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardPersonalDates(payload),
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardPersonalDates(payload);

      return data;
    },
    staleTime: ONE_HOUR
  });
};

export const useGetDashboardAgentCompetences = () => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardAgentCompetences(),
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardAgentCompetences();

      return data;
    },
    staleTime: ONE_HOUR
  });
};

export const useGetDashboardContractStatuses = () => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardContractStatuses(),
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardContractStatuses();

      return data;
    },
    staleTime: ONE_HOUR
  });
};

export const useGetDashboardContractStatistics = (payload: DashboardMinMaxDatesRequest) => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardContractStatistics(payload),
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardContractStatistics(payload);

      return data;
    },
    staleTime: ONE_HOUR
  });
};

export const useInfiniteFilterDashboardNotices = (filter?: DashboardNoticeFilterPageRequest) => {
  return useInfiniteQuery({
    queryKey: DASHBOARD_QUERY_KEYS.filterDashboardNotices(filter),
    queryFn: async ({ pageParam }) => {
      const { data } = await dashboardApi.filterDashboardNotices(pageParam);

      return data;
    },
    initialPageParam: { pageIndex: 0, pageSize: PageSizes.HUGE, ...filter },
    getNextPageParam: lastPage => {
      if (!lastPage.isLast) {
        return { pageIndex: lastPage.pageIndex + 1, pageSize: lastPage.pageSize };
      }

      return;
    }
  });
};

export const useCreateDashboardNotice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.createDashboardNotice(),
    mutationFn: async (payload: CreateUpdateDashboardNotice) => {
      const { data } = await dashboardApi.createDashboardNotice(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.filterDashboardNotices() });
      messageUtils.itemCreatedNotification();
    }
  });
};

export const useUpdateDashboardNotice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.updateDashboardNotice(),
    mutationFn: async (payload: EntityObject<CreateUpdateDashboardNotice>) => {
      const { data } = await dashboardApi.updateDashboardNotice(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.filterDashboardNotices() });
      messageUtils.itemUpdatedNotification();
    }
  });
};

export const useDeleteDashboardNotice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.deleteDashboardNotice(),
    mutationFn: (id: string) => {
      return dashboardApi.deleteDashboardNotice({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.filterDashboardNotices() });
      messageUtils.itemDeletedNotification();
    }
  });
};

export const useAddDashboardNoticeAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.addDashboardNoticeAttachments(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await dashboardApi.addDashboardNoticeAttachments(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        DASHBOARD_QUERY_KEYS.filterDashboardNotices(),
        (oldData: InfiniteData<DashboardNoticeFilterPageResult>) => ({
          ...oldData,
          pages: oldData.pages.map(page => {
            if (!page.pageData.some(notice => notice.id === mutatedPayload.id)) {
              return page;
            }

            return {
              ...page,
              pageData: page.pageData.map(notice => {
                if (notice.id !== mutatedPayload.id) {
                  return notice;
                }

                return {
                  ...notice,
                  attachments: data
                };
              })
            };
          })
        })
      );
    }
  });
};

export const useDeleteDashboardNoticeAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.deleteDashboardNoticeAttachment(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      const { data } = await dashboardApi.deleteDashboardNoticeAttachment(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(
        DASHBOARD_QUERY_KEYS.filterDashboardNotices(),
        (oldData: InfiniteData<DashboardNoticeFilterPageResult>) => ({
          ...oldData,
          pages: oldData.pages.map(page => {
            if (!page.pageData.some(notice => notice.id === mutatedPayload.id1)) {
              return page;
            }

            return {
              ...page,
              pageData: page.pageData.map(notice => {
                if (notice.id !== mutatedPayload.id1) {
                  return notice;
                }

                return {
                  ...notice,
                  attachments: notice.attachments.filter(att => att.id !== mutatedPayload.id2)
                };
              })
            };
          })
        })
      );
    }
  });
};

export const useDownloadDashboardNoticeAttachment = () => {
  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.downloadDashboardNoticeAttachment(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      const response = await dashboardApi.downloadDashboardNoticeAttachment(payload);
      openBlobFile(response);

      return response.data;
    }
  });
};

export const useSearchDashboardContacts = (filter: DashboardContactSearchRequest) => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.searchDashboardContacts(filter),
    queryFn: async () => {
      const { data } = await dashboardApi.searchDashboardContacts(filter);

      return data;
    },
    staleTime: ONE_HOUR
  });
};

export const useCreateDashboardContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.createDashboardContact(),
    mutationFn: async (payload: CreateUpdateDashboardContact) => {
      const { data } = await dashboardApi.createDashboardContact(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.searchDashboardContacts() });
      messageUtils.itemCreatedNotification();
    }
  });
};

export const useUpdateDashboardContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.updateDashboardContact(),
    mutationFn: async (payload: EntityObject<CreateUpdateDashboardContact>) => {
      const { data } = await dashboardApi.updateDashboardContact(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.searchDashboardContacts() });
      messageUtils.itemUpdatedNotification();
    }
  });
};

export const useDeleteDashboardContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.deleteDashboardContact(),
    mutationFn: async (id: string) => {
      return dashboardApi.deleteDashboardContact({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.searchDashboardContacts() });
      messageUtils.itemDeletedNotification();
    }
  });
};
